import React, { useState, useEffect } from "react";
import useTitanApp from "../../hooks/useTitanApp";
import {
  getPatientCommunicationByPatientId,
  addNewPatientCommunication,
  deletePatientCommunication,
} from "@/dispatchers/patientCommunicationDispatchers";

// ===== Types matching C# Models =====
export enum CommunicationType {
  Email = 0,
  Sms = 1,
  InApp = 2,
}

enum MessageStatus {
  Sent = 0,
  Delivered = 1,
  Read = 2,
  Failed = 3,
}

export enum NoteType {
  General = 0,
  Medical = 1,
  Administrative = 2,
  Communication = 3,
}

interface PatientCommunication {
  _id?: string;
  PatientId: string;
  Content: string;
  Type: NoteType;
  communicationType: CommunicationType;
  MessageStatus: MessageStatus;
  Author: string;
  Timestamp: string;
  IsArchive?: boolean;
  ArchiveBy?: string | null;
  ArchiveByName?: string | null;
  ArchiveOn?: string;
}

interface Note {
  id: string;
  content: string;
  timestamp: Date;
  author: string;
  type: "general" | "medical" | "administrative" | "communication";
  communicationType?: "email" | "sms" | "in-app";
  messageStatus?: "sent" | "delivered" | "read" | "failed";
  _id?: string; // MongoDB ID
  isArchive?: boolean;
  archiveBy?: string;
  archiveByName?: string;
  archiveOn?: Date;
}

export const noteTypes = [
  {
    value: "general",
    label: "General",
    color: "bg-gray-100 text-gray-800 border-gray-200",
  },
  {
    value: "medical",
    label: "Medical",
    color: "bg-red-100 text-red-800 border-red-200",
  },
  {
    value: "administrative",
    label: "Administrative",
    color: "bg-blue-100 text-blue-800 border-blue-200",
  },
  {
    value: "communication",
    label: "Communication",
    color: "bg-green-100 text-green-800 border-green-200",
  },
];

export const communicationTypes = [
  { value: "email", label: "Email", icon: "Mail" },
  { value: "sms", label: "SMS", icon: "Phone" },
  { value: "in-app", label: "In-App", icon: "MessageCircle" },
];

export const messageStatuses = [
  { value: "sent", label: "Sent", color: "text-blue-600" },
  { value: "delivered", label: "Delivered", color: "text-green-600" },
  { value: "read", label: "Read", color: "text-purple-600" },
  { value: "failed", label: "Failed", color: "text-red-600" },
];

// ===== Helper Functions =====

/**
 * Map enum number to string representation
 */
const mapCommunicationTypeToString = (
  type: CommunicationType,
): "email" | "sms" | "in-app" => {
  switch (type) {
    case CommunicationType.Email:
      return "email";
    case CommunicationType.Sms:
      return "sms";
    case CommunicationType.InApp:
      return "in-app";
    default:
      return "in-app";
  }
};

const mapNoteTypeToString = (
  type: NoteType,
): "general" | "medical" | "administrative" | "communication" => {
  switch (type) {
    case NoteType.General:
      return "general";
    case NoteType.Medical:
      return "medical";
    case NoteType.Administrative:
      return "administrative";
    case NoteType.Communication:
      return "communication";
    default:
      return "general";
  }
};

const mapMessageStatusToString = (
  status: MessageStatus,
): "sent" | "delivered" | "read" | "failed" => {
  switch (status) {
    case MessageStatus.Sent:
      return "sent";
    case MessageStatus.Delivered:
      return "delivered";
    case MessageStatus.Read:
      return "read";
    case MessageStatus.Failed:
      return "failed";
    default:
      return "sent";
  }
};

/**
 * Convert API response (PatientCommunication) to UI Note format
 * Handles both PascalCase (C# model) and camelCase (API response) field names
 */
export const mapApiResponseToNote = (
  apiResponse: any, // Allow any to handle both PascalCase and camelCase
): Note => {
  // Support both PascalCase (C# model) and camelCase (API response)
  const content = apiResponse.Content || apiResponse.content || "";
  const timestamp = apiResponse.Timestamp || apiResponse.timestamp || "";
  const author = apiResponse.Author || apiResponse.author || "";

  // Convert to numbers to ensure proper enum matching
  const type = Number(apiResponse.Type ?? apiResponse.type ?? 0);
  const communicationType = Number(
    apiResponse.communicationType ?? apiResponse.CommunicationType ?? 2,
  );
  const messageStatus = Number(
    apiResponse.MessageStatus ?? apiResponse.messageStatus ?? 0,
  );
  const isArchive = apiResponse.IsArchive ?? apiResponse.isArchive ?? false;
  const archiveBy = apiResponse.ArchiveBy ?? apiResponse.archiveBy ?? null;
  const archiveByName =
    apiResponse.ArchiveByName ?? apiResponse.archiveByName ?? null;
  const archiveOn = apiResponse.ArchiveOn ?? apiResponse.archiveOn ?? null;

  return {
    id: apiResponse._id || apiResponse.id || Date.now().toString(),
    content,
    timestamp: timestamp ? new Date(timestamp) : new Date(),
    author,
    type: mapNoteTypeToString(type),
    communicationType: mapCommunicationTypeToString(communicationType),
    messageStatus: mapMessageStatusToString(messageStatus),
    _id: apiResponse._id || apiResponse.id,
    isArchive,
    archiveBy: archiveBy || undefined,
    archiveByName: archiveByName || undefined,
    archiveOn: archiveOn ? new Date(archiveOn) : undefined,
  };
};

export const createNote = (
  content: string,
  author: string,
  type: Note["type"] = "general",
  communicationType?: Note["communicationType"],
  messageStatus?: Note["messageStatus"],
): Note => ({
  id: Date.now().toString(),
  content,
  timestamp: new Date(),
  author,
  type,
  communicationType,
  messageStatus,
});

export const formatTimeAgo = (timestamp: Date): string => {
  const now = new Date();
  const diff = now.getTime() - timestamp.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else {
    return "Just now";
  }
};

// ===== API Integration Hook =====

export interface UsePatientNotesDataReturn {
  patientID: string;
  notes: Note[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  addCommunication: (
    content: string,
    communicationType: CommunicationType,
    noteType?: NoteType,
  ) => Promise<Note | null>;
  deleteNotes?: (id: string) => Promise<boolean>;
}

export const usePatientNotesData = (): UsePatientNotesDataReturn => {
  const { patientState, patientCommunicationState } = useTitanApp();
  const { selectedPatientDetails } = patientState || {};
  const { patientID } = selectedPatientDetails || {};
  const {
    patientCommunicationData = [],
    isPatientCommunicationLoading = false,
  } = patientCommunicationState || {};

  const [notes, setNotes] = useState<Note[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Map API data from Redux to UI Notes format
  useEffect(() => {
    if (
      Array.isArray(patientCommunicationData) &&
      patientCommunicationData.length > 0
    ) {
      try {
        // Map API responses to UI Notes format
        const mappedNotes = patientCommunicationData.map((data) => {
          const mapped = mapApiResponseToNote(data);
          return mapped;
        });

        setNotes(mappedNotes);
      } catch (err) {
        console.error("❌ Error mapping communication data:", err);
        setError("Failed to process patient communications");
      }
    } else {
      // If no data from API, set empty array
      console.warn("⚠️ No patient communication data received");
      setNotes([]);
    }
  }, [patientCommunicationData]);

  // Fetch communications when patientID changes
  useEffect(() => {
    if (patientID) {
      getPatientCommunicationByPatientId(patientID);
    }
  }, [patientID]);

  const addCommunication = async (
    content: string,
    communicationType: CommunicationType,
    noteType: NoteType = NoteType.General,
  ): Promise<Note | null> => {
    if (!patientID) {
      setError("Patient ID is required");
      return null;
    }

    setIsSending(true);
    setError(null);

    try {
      // Ensure enum values are converted to numeric values for API
      const payload: any = {
        patientId: patientID,
        content: content,
        type: noteType,
        communicationType: communicationType,
        messageStatus: MessageStatus.Delivered,
        timestamp: new Date().toISOString(),
        isArchive: false,
        archiveBy: null,
        archiveByName: null,
        archiveOn: new Date().toISOString(),
      };
      // Call API to save communication
      const result = await addNewPatientCommunication(payload);

      if (result) {
        setNotes([]);
        await getPatientCommunicationByPatientId(patientID);
      } else {
        setError("Failed to save communication");
        console.error("❌ API returned null response");
        return null;
      }
    } catch (err) {
      setError("Error saving communication");
      console.error("❌ Error adding communication:", err);
      return null;
    } finally {
      setIsSending(false);
    }
  };

  const deleteNotes = async (id: string): Promise<boolean> => {
    await deletePatientCommunication(id);
    await getPatientCommunicationByPatientId(patientID);
    return true;
  };

  return {
    patientID,
    notes,
    isLoading: isPatientCommunicationLoading,
    isSending,
    error,
    addCommunication,
    deleteNotes,
  };
};
